import { nullSafeToString } from '../lib/util';

/*
 * Saved order metadata answers arrive on ProjectDTO.metadata as a JSON *string*, keyed by the answer's
 * position rather than by question:
 *
 *   {"0":{"QUESTION":"Order Type","ANSWER":"Restock Request","ADMINDISPLAY":{"list":[{"id":"OD"}]}}}
 *
 * (See MetaData and MetaDataMapperImpl#getMetadataValues server side.) Consumers want answers keyed by
 * the question they answered - the same key the write side uses - so the index is discarded here.
 *
 * ADMINDISPLAY is deliberately dropped: it belongs to the metadata *definition*, and
 * OrderMetadataOption already carries it for round-tripping on save. Reading it back from an answer
 * would give two sources of truth for the same setting.
 */

const QUESTION_KEY = 'QUESTION';
const ANSWER_KEY = 'ANSWER';

/**
 * Parse the saved order metadata answers off a project.
 *
 * @param {?string|Object} metadata - The raw `metadata` value from a project.
 * @returns {Object} A frozen map of question label to answer. Empty when there is nothing saved, or when the value cannot be read.
 */

export default function parseOrderMetadata (metadata) {
    if (!metadata) {
        return Object.freeze({});
    }

    let parsed;

    try {
        parsed = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    } catch (e) {
        // One unreadable metadata blob should not stop a project from loading.
        console.warn('Could not parse the saved order metadata on this project.');
        return Object.freeze({});
    }

    if (!parsed || typeof parsed !== 'object') {
        return Object.freeze({});
    }

    const answers = {};

    Object.keys(parsed).forEach(key => {
        const entry = parsed[key];

        if (!entry || typeof entry !== 'object') {
            return;
        }

        const question = nullSafeToString(entry[QUESTION_KEY]).trim();

        // An answer with no question cannot be matched back to an option, so it is unusable.
        if (!question) {
            return;
        }

        answers[question] = nullSafeToString(entry[ANSWER_KEY]);
    });

    return Object.freeze(answers);
}
